import { useCallback, useMemo, useReducer } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { DragDropContext } from '@hello-pangea/dnd';
import { ts, ts2 } from 'src/utils/translate';
import { cmToInch, roundToMax2 } from 'src/utils/imperialConverter';
import { isPlannerMetric } from 'src/components/Planner/planner/planner';
import { getRoomById } from 'src/components/Planner/planner/uDev';
import { toastError } from 'src/components/Planner/controller';
import {
    AdjustFrameState,
    buildInitialState,
    cancelAdjustFrame,
    confirmSave,
    detachFrames,
    FrameState,
    reducer,
} from 'src/pages/PlannerPage/Planner/dialogs/AdjustFrameDialog/adjustFrameActions';
import { FrameView } from 'src/pages/PlannerPage/Planner/dialogs/AdjustFrameDialog/FrameView';

interface AdjustFrameDialogProps {
    serialId: number;
    onClose: () => void;
}

// Frame-placement rules (ported from legacy framednd.jsx).
// FLOOR_MARGIN: minimum gap a frame must keep from the floor / ceiling.
// VERTICAL_DEVICE_STEP: extra height each additional device in a vertical frame
//   adds; used to widen the allowed height window accordingly.
// MIN_FRAME_DIST: two frames in the same area must be at least this far apart
//   in height, otherwise they overlap visually.
const FLOOR_MARGIN_CM = 10;
const FLOOR_MARGIN_INCH = 4;
const VERTICAL_DEVICE_STEP_CM = 8;
const VERTICAL_DEVICE_STEP_INCH = 3.5;
const MIN_FRAME_DIST_CM = 12;
const MIN_FRAME_DIST_INCH = 5;

// Niko Home Controller serializes at most a few devices per frame: 4 in
// horizontal, 3 in vertical. Anything above that is rejected by the planner.
const NHC_MAX_PER_HORIZONTAL_FRAME = 4;
const NHC_MAX_PER_VERTICAL_FRAME = 3;

function computeFrameError(
    frame: FrameState,
    framesArr: FrameState[],
    devices: any[],
): string | null {
    if (frame.deviceIds.length === 0) return null;
    const room = getRoomById(frame.roomId);
    if (!room) return null;

    const nhcCount = devices.reduce(
        (count, dev) => (dev?.serializable && dev?.is_nhc ? count + 1 : count),
        0,
    );
    const nhcLimit =
        frame.direction === 'vertical'
            ? NHC_MAX_PER_VERTICAL_FRAME
            : NHC_MAX_PER_HORIZONTAL_FRAME;
    if (nhcCount > nhcLimit) {
        return ts(
            'adjust_frame_nhc_limit',
            'Some devices cannot be combined in this frame due to Niko Home Controller serialization rules.',
        );
    }

    const metric = isPlannerMetric();
    const roomHeadroom = room.room_headroom;
    const currFrameHeight = metric ? frame.height : cmToInch(frame.height);

    const minHeightFromFloor = metric ? FLOOR_MARGIN_CM : FLOOR_MARGIN_INCH;
    const maxHeightFromFloor = metric
        ? roundToMax2(roomHeadroom) - FLOOR_MARGIN_CM
        : roundToMax2(cmToInch(roomHeadroom)) - FLOOR_MARGIN_INCH;
    const verticalStep = metric ? VERTICAL_DEVICE_STEP_CM : VERTICAL_DEVICE_STEP_INCH;
    const minDist = metric ? MIN_FRAME_DIST_CM : MIN_FRAME_DIST_INCH;
    const suffix = metric ? 'cm' : 'in';

    let minHeightVal = minHeightFromFloor;
    let maxHeightVal = maxHeightFromFloor;
    if (frame.direction === 'vertical') {
        minHeightVal += (frame.deviceIds.length - 1) * verticalStep;
        maxHeightVal -= (frame.deviceIds.length - 1) * verticalStep;
    }

    if (currFrameHeight < minHeightVal - 0.001) {
        return ts2(
            'adjust_frame_min_height',
            'The minimum height is {MIN} {SUFFIX}',
            { MIN: '' + minHeightVal, SUFFIX: suffix },
        );
    }
    if (currFrameHeight > maxHeightVal + 0.001) {
        return ts2(
            'adjust_frame_max_height',
            'The maximum height is {MAX} {SUFFIX}',
            { MAX: '' + maxHeightVal, SUFFIX: suffix },
        );
    }

    for (const otherFrame of framesArr) {
        if (otherFrame.id === frame.id || !otherFrame.deviceIds.length) continue;
        const otherHeight = metric ? otherFrame.height : cmToInch(otherFrame.height);
        if (Math.abs(otherHeight - currFrameHeight) < minDist - 0.2) {
            return ts2(
                'frm_err_dist',
                'The distance between frames must be at least {MIN_DIST} {SUFFIX}',
                { MIN_DIST: '' + minDist, SUFFIX: suffix },
            );
        }
    }
    return null;
}

export function AdjustFrameDialog({ serialId, onClose }: AdjustFrameDialogProps) {
    const initialState = useMemo(() => buildInitialState(serialId), [serialId]);

    if (!initialState) {
        return null;
    }

    return <AdjustFrameDialogInner initialState={initialState} onClose={onClose} />;
}

function AdjustFrameDialogInner({
    initialState,
    onClose,
}: {
    initialState: AdjustFrameState;
    onClose: () => void;
}) {
    const [state, dispatch] = useReducer(reducer, initialState);

    const framesArr = Object.values(state.frames);
    const framesSorted = [...framesArr].sort((a, b) => a.height - b.height);

    const devicesByFrame: Record<string, any[]> = {};
    framesArr.forEach((frame) => {
        devicesByFrame[frame.id] = frame.deviceIds
            .map((id) => state.devices[id])
            .filter(Boolean);
    });

    const frameErrors: Record<string, string | null> = {};
    framesArr.forEach((frame) => {
        frameErrors[frame.id] = computeFrameError(frame, framesArr, devicesByFrame[frame.id]);
    });

    const containsError =
        state.dragging ||
        framesArr.some((f) => !!frameErrors[f.id] || f.deviceIds.length > 5);
    const containsMoreFrames = framesArr.length > 1 && !framesArr.some((f) => f.isNew);
    const isLoading = state.loading !== false;

    const handleDetach = () => {
        dispatch({ type: 'SET_LOADING', payload: 'detach' });
        detachFrames(state)
            .catch(() => toastError(ts('adjust_frame_detach_failed', 'Could not detach frames.')))
            .finally(() => {
                onClose();
            });
    };

    const handleConfirm = () => {
        dispatch({ type: 'SET_LOADING', payload: 'confirm' });
        const origi = buildInitialState(state.serial.id);
        if (!origi) {
            onClose();
            return;
        }
        confirmSave(origi, state, true)
            .catch(() => toastError(ts('adjust_frame_save_failed', 'Could not save frame changes.')))
            .finally(() => {
                onClose();
            });
    };

    const handleDragStart = useCallback(() => dispatch({ type: 'ON_DRAG_START' }), []);
    const handleDragEnd = useCallback(
        (result: any) => dispatch({ type: 'ON_DRAG_END', payload: result }),
        [],
    );
    const setFrameHeight = useCallback(
        (frameId: string, cm: number) =>
            dispatch({ type: 'SET_HEIGHT', payload: { frameId, cm } }),
        [],
    );
    const toggleFrameDirection = useCallback(
        (frameId: string) =>
            dispatch({ type: 'TOGGLE_DIRECTION', payload: { frameId } }),
        [],
    );

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 10006,
                display: 'flex',
            }}
        >
            <Box sx={{ m: 'auto', userSelect: 'none' }}>
                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    {framesSorted.map((frame) => (
                        <FrameView
                            key={frame.id}
                            frame={frame}
                            devices={devicesByFrame[frame.id]}
                            inputBadReason={frameErrors[frame.id]}
                            onHeightChange={setFrameHeight}
                            onToggleDirection={toggleFrameDirection}
                        />
                    ))}
                </DragDropContext>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            p: 1,
                            background: 'white',
                            boxShadow: 2,
                            borderRadius: 1,
                        }}
                    >
                        {containsMoreFrames && (
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={isLoading}
                                onClick={handleDetach}
                            >
                                {ts('frm_detach', 'Detach frames')}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => { cancelAdjustFrame(); onClose(); }}
                            disabled={isLoading}
                        >
                            {ts('plt_cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={containsError || isLoading}
                            onClick={handleConfirm}
                        >
                            {ts('plt_confirm', 'Confirm')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
